import type { CandidateRejectionReason } from "../../common/types/CandidateRejectionReason";
import type { ReactNode } from "react";
import { nanoid } from "@reduxjs/toolkit";
import { api } from "../../app/api";
import { command } from "../../app/command";
import { query } from "../../app/query";
import filterFalsyParameters from "../../common/helpers/filterFalsyParameters";
import { EventPayload } from "../../common/types/Event";
import { Hits, Paginated } from "../../common/types/Paginated";
import { VariationSelection } from "../../common/types/apiTypes";
import { TalentIdFilter } from "../../common/types/commonTypes";
import { Nullable } from "../../common/types/nullable";
import { Options } from "../../common/types/requestOptions";
import {
  EmploymentDetails,
  WorkTimeBankSetting,
} from "../employment/employmentApi";
import { DefaultLunchBreak } from "../entries/entriesAPI";
import { LanguageCode } from "../localized/localizedApi";
import { Skill, WorkTitle } from "../orders/orderAPI";
import { SalaryType } from "../orders/types/salaryType";
import { PayslipsResponse } from "../salaries/payslips/payslipAPI";
import { SupplementsData } from "../supplements/supplementAPI";
import { PriceValueType } from "../tesPricingOverride/tesPricingOverrideAPI";
import { WorkHistory } from "../workHistory/types/queries";
import { EezyCompany } from "./../invoices/invoiceAPI";
import {
  AddBlacklistEntry,
  AddSelfServiceOrderPermission,
  AddTalentCertificate,
  AddTalentComment,
  AddTalentTraining,
  ApproveIntroducedTalentWithoutOrder,
  ApproveTalentCertificate,
  CancelTalentIntroductionWithoutOrder,
  CreateExclusivityEntry,
  CreateManualSalary,
  DeleteManualSalary,
  EditManualSalary,
  IntroduceTalentsToCustomerWithoutOrder,
  RejectTalentCertificate,
  RemoveTalentPaymentInformation,
  RemoveBlacklistEntry,
  RemoveExclusivityEntry,
  RemoveSelfServiceOrderPermission,
  RemoveTalentCertificate,
  RemoveTalentCertificateDocument,
  RemoveTalentComment,
  RemoveTalentTraining,
  SetTalentRecruiter,
  SetTalentWorkingAreaFromHome,
  UpdateBlacklistEntry,
  UpdateExclusivityEntry,
  UpdateSelfServiceOrderPermission,
  UpdateTalentAccount,
  UpdateTalentApplicationLanguage,
  UpdateTalentCertificate,
  UpdateTalentComment,
  UpdateTalentEducations,
  UpdateTalentEezyReview,
  UpdateTalentGdprProperties,
  UpdateTalentHrRepresentative,
  UpdateTalentLanguages,
  UpdateTalentRecommenders,
  UpdateTalentSalaryInformation,
  UpdateTalentStatus,
  UpdateTalentTaxDeductionCard,
  UpdateTalentTaxInformation,
  UpdateTalentTraining,
  UpdateTalentWorkRight,
} from "./types/commands";
import { TalentStatus, TalentWorkRightReason } from "./types/common";
import {
  BlacklistEntryAdded,
  BlacklistEntryRemoved,
  BlacklistEntryUpdated,
  CandidateWithoutOrderApprovedByCustomer,
  CandidateWithoutOrderIntroducedToCustomer,
  CandidateWithoutOrderIntroductionCancelled,
  ExclusivityEntryAdded,
  ExclusivityEntryRemoved,
  ExclusivityEntryUpdated,
  ManualSalaryAdded,
  ManualSalaryEdited,
  SelfServiceOrderPermissionAdded,
  SelfServiceOrderPermissionUpdated,
  TalentApplicationLanguageUpdated,
  TalentCertificateAdded,
  TalentCertificateApproved,
  TalentCertificateDocumentRemoved,
  TalentCertificateRejected,
  TalentCertificateRemoved,
  TalentCertificateUpdated,
  TalentCommentAdded,
  TalentCommentUpdated,
  TalentEducationsUpdated,
  TalentEezyReviewUpdated,
  TalentGdprPropertiesUpdated,
  TalentHrRepresentativeUpdated,
  TalentLanguagesUpdated,
  TalentRecommendersUpdated,
  TalentRecruiterUpdated,
  TalentSalaryInformationUpdated,
  TalentSelfServiceOrderPermissionDeleted,
  TalentStatusUpdated,
  TalentTaxDeductionCardUpdated,
  TalentTaxInformationUpdated,
  TalentTrainingAdded,
  TalentTrainingRemoved,
  TalentTrainingUpdated,
  TalentWorkingAreaEvents,
  TalentWorkRightUpdated,
} from "./types/events";
import {
  AssignmentsByTalentIdFilters,
  CandidateIntroductionsByTalentIdFilters,
  CertificateDefinition,
  CertificateDefinitionsFilters,
  CertificateDocumentDownloadUrlQuery,
  CertificateDocumentUploadUrlQuery,
  CvDocumentUploadUrlQuery,
  DocumentDownloadUrl,
  DocumentUploadUrl,
  ExclusivitiesByTalentIdFilters,
  OverlappableAssignmentsByTalentIdFilters,
  TalentCvParams,
  TalentHrRepresentativeAutocomplete,
  TalentHrRepresentativesAutocompleteFilters,
  TalentIdQueryParameter,
  TalentIntroduction,
  TalentLogEvent,
  TalentProfilePictureDownloadUrl,
  TalentProfilePictureDownloadUrlQuery,
  TalentSelfServiceOrderPermissionsResponse,
  TrainingDefinition,
  TrainingDefinitionsFilters,
} from "./types/queries";

export interface TalentAddress {
  street: string;
  country?: string;
  city: string;
  zip: string;
}

export interface TalentAccount {
  dob: string;
  version: number;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  address: TalentAddress;
  nationality?: string; // FIXME use NationalityCode as type?
  status: TalentStatus;
  tags?: string[];
  statusChanged?: string; // yyyy-MM-dd
  gracePeriodEndDate?: string; // yyyy-MM-dd
  redactionDate?: string; // yyyy-MM-dd
  profilePicture?: string | null;
}

export interface TalentWorkRight {
  version: number;
  reason: TalentWorkRightReason;
  start?: string;
  end?: string;
}

export interface TalentLanguages {
  values: Language[];
  version: number;
}

export interface TalentCoverLetterPayload {
  talentId: string;
  version: number;
  coverLetter: string;
}

export interface TalentWorkTitlesPayload {
  version: number;
  workTitles: string[];
}

export interface TalentSkillsPayload {
  version: number;
  skills: string[];
}

export interface WorkTitles {
  values: WorkTitle[];
  version: number;
}

export interface Skills {
  values: Skill[];
  version: number;
}

export interface Education {
  id: string;
  level: string;
  industry: string;
  degree: string;
  school: string;
  start: string;
  end?: string;
}

export interface Educations {
  values: Education[];
  version: number;
}

export interface CoverLetter {
  version: number;
  value: string;
}

export interface Recommenders {
  values: Recommender[];
  version: number;
}

export interface Recommender {
  id: string;
  name: string;
  title: string;
  company: string;
  phone: string;
  email: string;
  additionalInfo: string;
}

export interface TalentCertificateDocument {
  pathspec: string;
  filename: string;
  size: number;
  fileType: string;
  status: string;
}

export interface TalentCertificate {
  type: string;
  version: number;
  id: string;
  validTo?: string;
  grantedBy?: string;
  approvedBy?: string;
  approvedAt?: string;
  rejectedBy?: string;
  rejectedAt?: string;
  documents?: TalentCertificateDocument[];
  requiresDocument?: boolean;
}

export interface TalentTraining {
  id: string;
  version: number;
  type: string;
  completed: boolean;
  completedAt?: string;
  validTo?: string;
}

export interface TalentGdprProperties {
  version: number;
  disableAutomatching: boolean;
  forbidPersonalInfoUpdates: boolean;
}

export interface TalentApplicationLanguage {
  version: number;
  value: string;
}

export enum Gender {
  Female = "FEMALE",
  Male = "MALE",
  Empty = "notSelected",
}

export interface TalentSalaryInformation {
  version: number;
  receiver?: string;
  ssn?: string;
  iban?: string;
  bic?: string;
  gender?: Gender;
}

export interface TaxDeductionCard {
  updatedBy: string;
  updatedAt: string;
  validFrom: string;
  withholdingRate: string;
  incomeCeiling: string;
  additionalWithholdingRate: string;
}

export interface TalentTaxInformation {
  version: number;
  taxNonresidentAddress?: string;
  taxNumber?: string;
  insurancePaymentOutsideA1?: boolean;
  taxDeductionCard?: TaxDeductionCard;
}

export interface TalentTaxDeductionCard {
  version: number;
  taxDeductionCard: TaxDeductionCard;
}

export interface TalentComment {
  id: string;
  version: number;
  talentId: string;
  comment: string;
  dateTime: number;
  authorId: string;
  authorFirstName: string;
  authorLastName: string;
  relatedStatus?: TalentStatus;
}

export interface TalentHrRepresentative {
  id: string;
  version: number;
  firstName?: string;
  lastName?: string;
}

export interface TalentEezyReview {
  version: number;
  value: string;
}

export interface TalentVehicleAvailable {
  version: number;
  value: boolean;
}

export interface TalentWorkingAreas {
  config: {
    distanceFromHome?: number; // meters
    version: number;
  };
}

export interface TalentCV {
  pathspec: string;
  filename: string;
  size: number;
  fileType: string;
  status: string;
}

export interface Talent {
  account: TalentAccount;
  workRight?: TalentWorkRight;
  id: string;
  languages?: TalentLanguages;
  type: string;
  version: number;
  selected?: boolean;
  // TODO decide field names and contents
  workTitles?: WorkTitles;
  skills?: Skills;
  educations?: Educations;
  coverLetter?: CoverLetter;
  certificates?: TalentCertificate[];
  trainings?: TalentTraining[];
  applicationLanguage?: TalentApplicationLanguage;
  gdprProperties: TalentGdprProperties;
  recommenders?: Recommenders;
  salaryInformation?: TalentSalaryInformation;
  taxInformation?: TalentTaxInformation;
  taxDeductionCard?: TalentTaxDeductionCard;
  comments?: TalentComment[];
  hrRepresentative?: TalentHrRepresentative;
  eezyReview: TalentEezyReview;
  vehicleAvailable: TalentVehicleAvailable;
  recruitedByCustomerCompany?: string; // customerCompanyId
  cv?: TalentCV;
  // For list view
  recruitmentAreaName?: string;
  workingAreas?: TalentWorkingAreas;
  exclusivityEntryIds?: string[];
  memberSince?: number;
  llt?: EpochTimeStamp;
}

export interface Certificate {
  id: string;
  name: string;
  requiresDocument: boolean;
}

export interface CertificateFilters {
  term?: string;
  language: string;
}

export interface TalentFilters {
  talentId?: string;
}

export type TalentsSortBy =
  | "name_asc"
  | "name_desc"
  | "memberSince_asc"
  | "memberSince_desc"
  | "score";

export interface TalentsBaseFilters {
  pageSize: string;
  search?: string;
  status?: string;
  hrRepresentative?: string;
  talentTags?: string;
  areas?: string;
  orderQueues?: string;
  workTitleIds?: string;
  skillIds?: string;
  city?: string;
  certificateIds?: string;
  trainingIds?: string;
  languageSkillIds?: string;
  vehicleAvailable?: string;
  cursor?: Nullable<string>;
}

export interface TalentsAutocompleteFilters extends TalentsBaseFilters {}

export interface TalentsFullSearchFilters extends TalentsBaseFilters {
  searchTerms?: string;
  customerOrgId?: string;
  assignmentType?: string;
  assignmentActiveFrom?: string;
  assignmentActiveTo?: string;
  assignmentInactiveFrom?: string;
  assignmentInactiveTo?: string;
  sortBy?: TalentsSortBy;
}

export interface TalentAddressResponse {
  id: string;
  name: string;
  payload: {
    address: TalentAddress;
  };
  version: number;
}

export type LanguageSkillLevel =
  | "BASIC"
  | "LIMITED"
  | "GOOD"
  | "EXCELLENT"
  | "NATIVE";

export type LanguageSkillCode =
  | "ar"
  | "en"
  | "es"
  | "hi"
  | "it"
  | "ja"
  | "zh"
  | "no"
  | "pt"
  | "fr"
  | "sv"
  | "de"
  | "fi"
  | "da"
  | "uk"
  | "ru"
  | "sgn"
  | "et";

export interface Language {
  id: string;
  languageCode: LanguageSkillCode;
  skillLevel: LanguageSkillLevel;
}

export interface CertificateDocument {
  content: Blob;
  url: string;
}

export interface Assignment {
  assignmentId: string;
  customerOrgName: string;
  workLocationName: string;
  workTitleId: string;
  priceOverride: string;
  startDate: string;
  endDate?: string;
  cancellation?: AssignmentCancellation;
  invoicingPriceOverride?: string;
}

export interface CancelledEmployment extends EmploymentAndAttachmentRow {
  isShift: boolean;
  employerId: string;
  employerName: string;
  tesId: string;
}

export interface EmploymentsHierarchyResponse {
  hierarchy: EmploymentsHierarchy[];
  cancelledEmployments: CancelledEmployment[];
}

export interface EmploymentsHierarchy {
  continuousEmploymentId?: string;
  employerId: string; // eezyCompanyId
  employerName: string;
  employments: EmploymentAndAttachmentRow[];
  endDate: string;
  startDate: string;
  tesId: string;
  employmentType?: string;
  isShift: boolean;
}

export interface EmploymentAndAttachmentRow {
  employmentId: string;
  startDate: string;
  endDate: string | null;
  assignments: Assignment[];
  targetedAssignments: Assignment[];
  isCancelled: boolean;
}

export interface SalaryPaymentInfo {
  iban: string;
  bic: string;
  ssn: string;
}

export interface CostCenter {
  id: string;
  name: string;
}

export interface AssignmentVariationSelection {
  tesSelections: VariationSelection[];
}

export interface AssignmentCancellation {
  reason: string;
  talentGetsPaid: boolean;
}

export interface AssignmentOrderAttributes {
  isExpress: boolean;
  isSelfService: boolean;
  isEarlyBird: boolean;
}

export interface AssignmentDetails {
  version: number;
  start: number;
  end?: number;
  talentFirstName: string;
  talentLastName: string;
  contractId: string;
  contractName: string;
  tesId: string;
  orderAttributes?: AssignmentOrderAttributes;
  isCustomerRecruit?: boolean;
  tesSelections: VariationSelection[];
  workTitleId: string;
  workLocationId: string;
  workLocationName: string;
  customerOrgId: string;
  customerOrgName: string;
  custCostCenters: CostCenter[];
  cancellation?: AssignmentCancellation;
  salaryOverride?: { [key: string]: string };
  availableEntryTypes: string[];
  invoicingPriceOverride?: string;
  isShift: boolean;
  defaultLunchBreak?: DefaultLunchBreak;
}

export interface SupplementPriceOverride {
  value: string;
  unit: PriceValueType;
}

export interface DateValuePair {
  date: string;
  value: string;
}

export interface AssignmentSupplementPriceScheduleQuery {
  assignmentId: string;
  code: string;
}

export interface AssignmentSupplementPriceSchedule {
  code: string;
  unit: PriceValueType;
  schedule: DateValuePair[];
}

export interface AssignmentSupplementPriceOverridesOverviewQuery {
  assignmentId: string;
  date: string;
}

export interface AssignmentSupplementPriceOverridesOverviewItem {
  code: string;
  count: number;
  currentValue: string | null;
  nextDate: string | null;
  nextValue: string | null;
  unit: string | null;
}

export interface AssignmentById {
  talentId: string;
  employmentId: string;
  assignmentId: string;
  orderRowId: string;
  details: AssignmentDetails;
  supplements: SupplementsData;
  employmentSalary?: { [key: string]: string };
  employmentSalaryType?: SalaryType;
  employmentStartDate?: string;
  employmentEndDate?: string | null;
  eezyCompanyId: string;
  isShift?: boolean;
  bundleId?: string;
  supplementPriceOverrides?: { [key: string]: SupplementPriceOverride };
  timeBankSettings?: WorkTimeBankSetting[];
  flexibleHoursSettings?: WorkTimeBankSetting[];
  isTargetedShift?: boolean;
}

export interface OverlappableAssignmentById extends AssignmentById {
  failures?: string[];
}

export interface OverlappableContEmp {
  talentId: string;
  employerName: string;
  employments: OverlappableEmployment[];
  readonly failed: boolean;
}

export interface OverlappableEmployment {
  startDate: string; // ISO 8601 Date string
  endDate?: string; // ISO 8601 Date string or undefined
  employmentId: string;
  details: OverlappableEmploymentDetails;
  assignments: OverlappableAssignment[];
  failures: string[];
  readonly failed: boolean;
  employerId: string;
  targetedCustomerCompanyIds: string[];
}

export interface OverlappableEmploymentDetails {
  employerName: EmploymentDetails["employerName"];
  workTimeType: EmploymentDetails["workTimeType"];
  annualHolidayAccumulationRule: EmploymentDetails["annualHolidayAccumulationRule"];
  salary: {
    ranges: Array<{
      components: EmploymentDetails["salary"]["ranges"][number]["components"];
    }>;
  };
  salaryType: EmploymentDetails["salaryType"];
  isAveragingPeriodInUse: EmploymentDetails["isAveragingPeriodInUse"];
  periodType: EmploymentDetails["periodType"];
  periodStartDate: EmploymentDetails["periodStartDate"];
  agreedHours: EmploymentDetails["agreedHours"];
  talentAgreesExtraWork: EmploymentDetails["talentAgreesExtraWork"];
  probationEndDate: EmploymentDetails["probationEndDate"];
}

export interface OverlappableAssignment {
  assignmentId: string;
  details: Pick<
    AssignmentDetails,
    "start" | "end" | "tesId" | "workTitleId" | "workLocationName"
  >;
  supplements: SupplementsData;
  startDate: string; // ISO 8601 Date string
  endDate?: string; // ISO 8601 Date string or undefined
  supplementPriceOverrides: { [key: string]: SupplementPriceOverride };
  failures: string[];
}

export interface ChgAssignmentTesSel {
  assignmentId: string;
  version: number;
  tesSelections: VariationSelection[];
}

export interface PostAssignmentSupplement {
  assignmentId: string;
  version: number;
  code: string;
  mode?: string;
}

export interface AssignmentSupplementResponse {
  assignmentId: string;
}

export enum ManualSalaryUnitType {
  NONE = "None",
  H = "H",
  PCS = "PCS",
  KM = "KM",
}

export enum ManualSalaryRepeatType {
  MONTHLY = "MONTHLY",
  NONE = "NONE",
}

export interface ManualSalary {
  manualSalaryId: string;
  date: string;
  amount: string;
  unitCost: string;
  unit: ManualSalaryUnitType | null;
  repeatType: ManualSalaryRepeatType;
  repeatUntil?: string;
  description: string | null;
  expenseTypeCode: string;
  payslipId?: string;
  assignmentId: string | null;
  customerCostCenterId: string | null;
  eezyCostCenterId: string | null;
  isBillable: boolean;
  version: number;
  createdAt?: number | null;
}

export interface ManualSalariesResponse {
  manualSalaries: ManualSalary[];
}

export interface EmploymentDetailsForManualSalaryResponse {
  assignments: {
    [id: string]: {
      startDate: string;
      workTitleId: string;
      workLocationName: string;
      customerCostCenters: {
        [id: string]: string;
      };
      isShift: boolean;
      isTargetedShift?: boolean;
    };
  };
  eezyCostCenters: {
    id: string;
    code: string;
  }[];
  expenseTypeCodes: {
    [id: string]: {
      code: string;
      unit: string;
    }[];
  };
}

export interface EezyCompanyAutocompleteFilters {
  name: string;
}

export interface TradeUnionsAutocompleteFilters {
  name: string;
}

export interface TalentUnionPayment {
  id: string;
  talentId: string;
  tradeUnionId: string;
  eezyCompanyId: string;
  tradeUnionName: string;
  tradeUnionAcronym: string;
  eezyCompanyName: string;
  validFrom: string;
  validTo: string;
}

export interface TalentUnionCreatePayment {
  talentId: string;
  tradeUnionId: string;
  eezyCompanyId: string;
  validFrom: string;
  validTo: string;
}

export interface UnionOption {
  id: string;
  parentId: string | null;
  parentUnionName?: string | null;
  name: string;
  acronym: string;
  tradeUnionNumber: string;
}

export interface ChangeAssignmentOrderAttributes {
  assignmentId: string;
  version: number;
  orderAttributes: {
    isExpress: boolean;
    isSelfService: boolean;
    isEarlyBird: boolean;
  };
  isCustomerRecruit: boolean;
}

export interface ChangeAssignmentCostCenters {
  assignmentId: string;
  version: number;
  custCostCenterIds: string[];
}

// thunk type
export interface EnhancedChangeAssignmentCostCenters {
  assignmentId: string;
  version: number;
  custCostCenters: CostCenter[];
}

export interface AssignmentOrderAttributesChanged {
  orderAttributes: AssignmentOrderAttributes;
}

export interface AssignmentIsCustomerRecruitChanged {
  isCustomerRecruit: boolean;
}

export type AssignmentOrderAttributeEvents =
  | AssignmentOrderAttributesChanged
  | AssignmentIsCustomerRecruitChanged;

export interface AssignmentCostCentersChanged {
  ids: string[];
}

export interface ChangeAssignmentSalaryOverride {
  assignmentId: string;
  version: number;
  salaryOverride: Nullable<{ [key: string]: string }>;
}

export interface AssignmentSalaryOverrideAdded {
  salaryOverride: { [key: string]: string };
}

export interface AssignmentSalaryOverrideRemoved {}

export type AssignmentSalaryOverrideChanged =
  | AssignmentSalaryOverrideAdded
  | AssignmentSalaryOverrideRemoved;

export interface ChangeAssignmentInvPrice {
  assignmentId: string;
  version: number;
  unitPrice: string | null;
}

export interface SetAssignmentEnd {
  assignmentId: string;
  version: number;
  endDate: string;
  reason: string;
}

export interface UpdateAssignmentRange {
  assignmentId: string;
  version: number;
  startDate: string;
  endDate: string | null;
  reason: string;
}

export interface DateRange {
  start: string;
  end: string;
}

export interface UpdateContinuousEmploymentRange {
  continuousEmploymentId: string;
  range: DateRange;
}

/**
 * basically assgnRangeUpd event payload
 */
export interface AssignmentRangeUpdated {
  oldRange: {
    start: number;
    end: number;
  };
  newRange: {
    start: number;
    end: number;
  };
  reason: string;
}

export interface UpdateTalentVehicleAvailable {
  talentId: string;
  version: number;
  vehicleAvailable: boolean;
}

export interface AssignmentInvPriceAdded {
  unitPrice: string;
}

export interface AssignmentInvPriceRemoved {}

export type AssignmentInvPriceChanged =
  | AssignmentInvPriceAdded
  | AssignmentInvPriceRemoved;

export interface TalentVehicleAvailableUpdated {
  vehicleAvailable: boolean;
}

export type LocalStorageAttachment = {
  id: string;
  uploadStarted: number;
  path: string;
  pathId: string[];
  filename: string;
  status: string;
};

export interface PostSplitAssignment {
  assignmentId: string;
  version: number;
  splitDate: string;
  reason: string;
}

export interface TalentBlacklist {
  id: string;
  talentId: string;
  blockingParty: string;
  createdAt: string;
  workTitles: WorkTitle[];
  customerCompanyId?: string;
  customerCompanyName?: string;
  customerOrganizationId?: string;
  customerOrganizationName?: string;
  workLocationId?: string;
  workLocationName?: string;
  reason?: string;
  version: number;
}

export interface TalentExclusivity {
  id: string;
  talentId: string;
  version: number;
  customerCompanyId: string;
  customerCompanyName: string;
  customerOrganizationId?: string;
  customerOrganizationName?: string;
  workLocationId?: string;
  workLocationName?: string;
  workTitles: WorkTitle[];
  createdAt?: string;
}

export interface TalentJobApplication {
  eezyRejectionReason?: CandidateRejectionReason | null;
  eezyRejectionComment?: string | null;
  id: string;
  jobAdId: string;
  bundleId: string | null;
  title: JSX.Element;
  startDate?: string;
  endDate: string;
  locations: { city: string; country: string }[];
  customerCompanyName: string;
  contactPerson: {
    id: string;
    name: string;
    email: string;
  };
  appliedAt?: EpochTimeStamp;
  hasCoverLetter: boolean;
  eezyAccepted: boolean | null;
  version: number;
}

export interface TalentJobApplicationRowItem
  extends Omit<
    TalentJobApplication,
    "jobAdId" | "locations" | "contactPerson"
  > {
  location: string;
  contactPersonName: string;
  status: ReactNode;
  url: string;
}

export interface TalentJobApplications {
  ongoing: TalentJobApplication[];
  ended: TalentJobApplication[];
}

export interface TalentJobApplicationsParams {
  talentId: string;
  pageSize?: string;
}

export interface TalentJobApplicationsResponse {
  data: TalentJobApplications;
  hits: Hits;
}

export interface WorkAgreementTemplateDataParams {
  employmentId: string;
  talentId: string;
  language: LanguageCode;
}

export interface WorkAgreementTemplateData {
  bundleId: string;
  startDate: string;
  endDate?: string;
  hasTrialPeriod: boolean;
  trialPeriodEnds?: string;
  employer: {
    companyName: string;
    businessId: string;
    address: {
      street: string;
      city: string;
      zip: string;
      country: string;
    };
  };
  employee: {
    firstName: string;
    lastName: string;
    ssn: string;
    phone: string;
    email: string;
    bankAccount: string;
    address: {
      street: string;
      city: string;
      zip: string;
      country: string;
    };
  };
  talentId: string;
  employerContact: {
    firstName: string;
    lastName: string;
    title: string;
    emailAddress: string;
    companyName: string;
  };
  customerOrgName: string;
  customerContacts: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  }[];
  workTitle: string;
  workLocation: string;
  reasonForTempEmployment: string;
  tesName: string;
  baseSalary: string;
  salaryComponents: {
    name: string;
    value: string;
  }[];
  isHourlySalary: boolean;
  totalSalary?: string;
  monthlySalary?: string;
  isFullTime: boolean;
  isPeriodicWorkTime: boolean;
  workTimeDescription: string;
  workTypeDescription: string;
  agreedHours: {
    minHours: string;
    maxHours: string;
    weeksInPeriod: string;
  };
  averagingSystemInUse: boolean;
  talentAgreesExtraWork: boolean;
  extraInformationAgreed: string;
  isAccepted: boolean;
  acceptedAt?: string;
  createdAt: string;
  contract: {
    id: string;
    eezyCompanyName: string;
  };
}

export interface TalentPreview {
  talentId: string;
  talent: {
    account: TalentAccount;
    languages?: TalentLanguages;
    workTitles?: WorkTitles;
    skills?: Skills;
    certificates?: TalentCertificate[];
    trainings?: TalentTraining[];
    eezyReview: TalentEezyReview;
    vehicleAvailable: TalentVehicleAvailable;
  };
  workHistory: WorkHistory[];
  comments: TalentComment[];
  introductionsWithoutOrder: Paginated<TalentIntroduction[]>;
  introductionsWithOrder: Paginated<TalentIntroduction[]>;
}

export interface TalentLog {
  userId: string;
  userName: string;
  userType: string;
  time: EpochTimeStamp;
  name: string;
  payload: any;
}

export const talentIsDisabled = (talent: Talent): boolean =>
  talent.account.status === TalentStatus.DISABLED ||
  talentIsDeactivated(talent);

export const talentIsDeactivated = (talent: Talent): boolean =>
  talent.account.status === TalentStatus.DEACTIVATED;

export const listTalents = ({ requestId }: Options = {}, ids?: string[]) => {
  const queryParams = ids?.length ? { ids: ids.join(",") } : undefined;
  return api.get<Talent[]>("/projections/qry/talentsList", {
    requestId,
    queryParams,
    auth: true,
  });
};

export const getTalentUnionPayments = (
  talentId: string,
  { requestId }: Options,
) => {
  return api.get<TalentUnionPayment[]>(
    "/projections/qry/talentUnionPaymentsByTalentId",
    {
      requestId,
      queryParams: {
        talentId: talentId,
      },
      auth: true,
    },
  );
};

export const postCreateTalentUnionPayment = (
  payment: TalentUnionCreatePayment,
  { requestId }: Options,
) => {
  return api.post<TalentUnionCreatePayment[]>(
    "/invoicing/cmd/createTalentUnionPayment",
    {
      requestId,
      body: {
        ...payment,
      },
      auth: true,
    },
  );
};

export const postUpdateTalentUnionPayment = (
  payment: TalentUnionPayment,
  { requestId }: Options,
) => {
  return api.post<TalentUnionPayment[]>(
    "/invoicing/cmd/updateTalentUnionPayment",
    {
      requestId,
      body: {
        ...payment,
      },
      auth: true,
    },
  );
};

export const postDeleteTalentUnionPayment = (
  paymentId: string,
  { requestId }: Options,
) => {
  return api.post<any>("/invoicing/cmd/deleteTalentUnionPayment", {
    requestId,
    body: {
      id: paymentId,
    },
    auth: true,
  });
};

export const getAssignmentById = (
  assignmentId: string,
  { requestId }: Options,
) => {
  return api.get<AssignmentById>("/projections/qry/assignmentById", {
    requestId,
    queryParams: {
      id: assignmentId,
    },
    auth: true,
  });
};

export const getAssignmentSupplementPriceOverridesOverview = (
  queryParams: AssignmentSupplementPriceOverridesOverviewQuery,
  options: Options = {},
): Promise<AssignmentSupplementPriceOverridesOverviewItem[]> =>
  query<
    AssignmentSupplementPriceOverridesOverviewQuery,
    AssignmentSupplementPriceOverridesOverviewItem[]
  >(
    "/projections/qry/assignmentSupplementPriceOverridesOverview",
    queryParams,
    options,
  );

export const getAssignmentSupplementPriceOverrideScheduleByCode = (
  queryParams: AssignmentSupplementPriceScheduleQuery,
  options: Options = {},
): Promise<AssignmentSupplementPriceSchedule | null> =>
  query<
    AssignmentSupplementPriceScheduleQuery,
    AssignmentSupplementPriceSchedule | null
  >(
    "/projections/qry/assignmentSupplementPriceOverrideScheduleByCode",
    queryParams,
    options,
  );

export const getAssignmentSupplementPriceOverrideScheduleByCodeMock = (
  _queryParams: AssignmentSupplementPriceScheduleQuery,
): Promise<AssignmentSupplementPriceSchedule> =>
  Promise.resolve({
    code: _queryParams.code,
    unit: "FIXED_PRICE",
    schedule: [
      { date: "2026-01-01", value: "13.45" },
      { date: "2026-02-01", value: "14.22" },
    ],
  });

export const getAssignmentSupplementPriceOverridesOverviewMock = (
  _queryParams: AssignmentSupplementPriceOverridesOverviewQuery,
): Promise<AssignmentSupplementPriceOverridesOverviewItem[]> =>
  Promise.resolve([
    {
      code: "erikoislisä",
      count: 1,
      currentValue: "0.0410",
      nextDate: null,
      nextValue: null,
      unit: "SALARY_MULTIPLIER",
    },
    {
      code: "erikoisolosuhdelisä",
      count: 2,
      currentValue: "2.10",
      nextDate: "2023-01-04",
      nextValue: "2.40",
      unit: "FIXED_PRICE",
    },
    {
      code: "erikoisurakka",
      count: 1,
      currentValue: null,
      nextDate: "2026-06-01",
      nextValue: "3.50",
      unit: "SALARY_MULTIPLIER",
    },
  ]);

export const splitAssignment = (
  splitAssignmentData: PostSplitAssignment,
  { requestId }: Options,
) => {
  return api.post<any>("/working/cmd/splitAssignment", {
    requestId,
    body: {
      ...splitAssignmentData,
    },
    auth: true,
  });
};

export const chgAssignmentTesSel = (
  payload: ChgAssignmentTesSel,
  { requestId }: Options,
): Promise<EventPayload<AssignmentVariationSelection>> =>
  api
    .post<EventPayload<AssignmentVariationSelection>[]>(
      "/working/cmd/chgAssignmentTesSel",
      {
        requestId,
        body: payload,
        auth: true,
      },
    )
    .then((res) => res.data[0]);

export const getTalentById = (
  TalentFilters: TalentFilters,
  { requestId }: Options,
) => {
  const { talentId } = TalentFilters;
  return api.get<Talent>("/projections/qry/talentById?id=" + talentId, {
    requestId,
    auth: true,
  });
};

export const getTalentPreviewById = (
  params: TalentIdQueryParameter,
  options: Options = {},
): Promise<TalentPreview> =>
  query<TalentIdQueryParameter, TalentPreview>(
    "/projections/qry/talentPreview",
    params,
    options,
  );

export const getTalentComments = async (
  queryParameters: TalentIdQueryParameter,
  { requestId }: Options,
): Promise<TalentComment[]> => {
  return api
    .get<
      TalentComment[]
    >("/projections/qry/talentComments?talentId=" + queryParameters.talentId, { requestId, auth: true })
    .then((res) => res.data);
};

export const searchTalentsAutocomplete = (
  queryParams: TalentsAutocompleteFilters,
  { requestId }: Options,
) => {
  return api.get<Paginated<Talent[]>>("/projections/qry/talentsAutocomplete", {
    requestId,
    queryParams: {
      ...filterFalsyParameters(queryParams),
      search: queryParams.search || "",
    },
    auth: true,
  });
};

export const searchTalentsFull = (
  queryParams: TalentsFullSearchFilters,
  { requestId }: Options,
) => {
  return api.get<Paginated<Talent[]>>("/projections/qry/talentsSearch", {
    requestId,
    queryParams: filterFalsyParameters(queryParams),
    auth: true,
  });
};

export const getEmploymentsHierarchy = (
  talentId: string,
  { requestId }: Options,
) => {
  return api.get<EmploymentsHierarchyResponse>(
    "/projections/qry/employmentsHierarchy2",
    {
      requestId,
      queryParams: {
        talentId: talentId,
      },
      auth: true,
    },
  );
};

export const addAssignmentSupplement = async (
  supplementData: PostAssignmentSupplement,
  { requestId }: Options,
): Promise<any> => {
  return await api
    .post<any>("/working/cmd/addAssignmentSupplement", {
      requestId,
      auth: true,
      body: {
        ...supplementData,
      },
    })
    .then((res) => {
      return res;
    });
};

export const editAssignmentSupplement = async (
  supplementData: PostAssignmentSupplement,
  { requestId }: Options,
) => {
  return await api
    .post<AssignmentSupplementResponse>(
      "/working/cmd/chgAssignmentSupplementMode",
      {
        requestId,
        auth: true,
        body: {
          ...supplementData,
        },
      },
    )
    .then((res) => {
      return res;
    });
};

export const deleteAssignmentSupplement = async (
  supplementData: PostAssignmentSupplement,
  { requestId }: Options,
): Promise<any> => {
  return await api
    .post<AssignmentSupplementResponse>(
      "/working/cmd/delAssignmentSupplement",
      {
        requestId,
        auth: true,
        body: {
          ...supplementData,
        },
      },
    )
    .then((res) => {
      return res;
    });
};

export const getSalaryPaymentInfo = (
  talentId: string,
  { requestId }: Options,
) => {
  return api.get<SalaryPaymentInfo>("/projections/qry/salaryPaymentInfo", {
    requestId,
    queryParams: {
      id: talentId,
    },
    auth: true,
  });
};

// TODO move to payslipAPI
export const getPayslipsOverview = (
  talentId: string,
  { requestId }: Options,
) => {
  return api.get<PayslipsResponse>("/projections/qry/payslipsOverview", {
    requestId,
    queryParams: {
      talentId: talentId,
      // pageSize:
      // cursor:
    },
    auth: true,
  });
};

export const postUpdateTalentAddress = async (
  address: TalentAddress,
  talentId: string,
  version: number,
  { requestId }: Options,
): Promise<TalentAddressResponse> => {
  const params = {
    requestId,
    auth: true,
    body: {
      version,
      talentId,
      address,
    },
  };

  return await api
    .post<TalentAddressResponse[]>("/identity/cmd/updateTalentAddress", params)
    .then((res) => {
      const { id, name, version, payload } = res.data[0];
      const { address } = payload;

      return {
        id,
        name,
        payload: {
          address,
        },
        version,
      };
    })
    .catch((error) => error.name);
};

export const postUpdateTalentWorkRight = async (
  command: UpdateTalentWorkRight,
  { requestId }: Options,
): Promise<EventPayload<TalentWorkRightUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body: command,
  };

  return await api
    .post<
      EventPayload<TalentWorkRightUpdated>[]
    >("/matching/cmd/updateTalentWorkRight", params)
    .then((res) => {
      return res.data[0];
    });
};

export const postUpdateTalentCoverLetter = async (
  payload: TalentCoverLetterPayload,
  { requestId }: Options,
): Promise<EventPayload<TalentCoverLetterPayload>> => {
  const params = {
    requestId,
    auth: true,
    body: payload,
  };

  return await api
    .post<
      EventPayload<TalentCoverLetterPayload>[]
    >("/matching/cmd/updateTalentCoverLetter", params)
    .then((res) => {
      return res.data[0];
    });
};

export const postUpdateTalentWorkTitles = async (
  data: { talentId: string; version: number; workTitles: WorkTitle[] },
  { requestId }: Options,
): Promise<EventPayload<TalentWorkTitlesPayload>> => {
  const workTitleIds = data.workTitles.map((workTitle) => workTitle.id);

  const params = {
    requestId,
    auth: true,
    body: {
      talentId: data.talentId,
      version: data.version,
      workTitles: workTitleIds,
    },
  };

  return await api
    .post<
      EventPayload<TalentWorkTitlesPayload>[]
    >("/matching/cmd/updateTalentWorkTitles", params)
    .then((res) => {
      return res.data[0];
    });
};

export const postUpdateTalentSkills = async (
  data: { talentId: string; version: number; skills: Skill[] },
  { requestId }: Options,
): Promise<EventPayload<TalentSkillsPayload>> => {
  const params = {
    requestId,
    auth: true,
    body: {
      talentId: data.talentId,
      version: data.version,
      skills: data.skills.map((skill) => skill.id),
    },
  };

  return await api
    .post<
      EventPayload<TalentSkillsPayload>[]
    >("/matching/cmd/updateTalentSkills", params)
    .then((res) => res.data[0]);
};

export const postUpdateTalentEducations = async (
  commandPayload: UpdateTalentEducations,
  { requestId }: Options,
): Promise<EventPayload<TalentEducationsUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body: commandPayload,
  };
  return await api
    .post<
      EventPayload<TalentEducationsUpdated>[]
    >("/matching/cmd/updateTalentEducations", params)
    .then((res) => res.data[0]);
};

export const postUpdateTalentRecommenders = async (
  commandPayload: UpdateTalentRecommenders,
  { requestId }: Options,
): Promise<EventPayload<TalentRecommendersUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body: commandPayload,
  };

  return await api
    .post<
      EventPayload<TalentRecommendersUpdated>[]
    >("/matching/cmd/updateTalentRecommenders", params)
    .then((res) => res.data[0]);
};

export const postUpdateTalentLanguages = async (
  payload: UpdateTalentLanguages,
  { requestId }: Options,
): Promise<EventPayload<TalentLanguagesUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body: payload,
  };

  return await api
    .post<
      EventPayload<TalentLanguagesUpdated>[]
    >("/matching/cmd/updateTalentLanguages", params)
    .then((res) => res.data[0]);
};

export const postUpdateTalentApplicationLanguage = async (
  payload: UpdateTalentApplicationLanguage,
  { requestId }: Options,
): Promise<EventPayload<TalentApplicationLanguageUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body: payload,
  };

  return await api
    .post<
      EventPayload<TalentApplicationLanguageUpdated>[]
    >("/identity/cmd/updateTalentApplicationLanguage", params)
    .then((res) => res.data[0]);
};

export const postUpdateTalentGdprProperties = async (
  payload: UpdateTalentGdprProperties,
  { requestId }: Options,
) => {
  const params = {
    requestId,
    auth: true,
    body: payload,
  };

  return await api
    .post<
      EventPayload<TalentGdprPropertiesUpdated>[]
    >("/identity/cmd/updateTalentGdprProperties", params)
    .then((res) => res.data[0]);
};

export const postAddTalentComment = async (
  payload: AddTalentComment,
  { requestId }: Options,
): Promise<EventPayload<TalentCommentAdded>> => {
  const params = {
    requestId,
    auth: true,
    body: payload,
  };

  return await api
    .post<
      EventPayload<TalentCommentAdded>[]
    >("/matching/cmd/addTalentComment", params)
    .then((res) => res.data[0]);
};

export const postUpdateTalentComment = async (
  payload: UpdateTalentComment,
  { requestId }: Options,
): Promise<EventPayload<TalentCommentUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body: payload,
  };

  return await api
    .post<
      EventPayload<TalentCommentUpdated>[]
    >("/matching/cmd/updateTalentComment", params)
    .then((res) => res.data[0]);
};

export const postRemoveTalentComment = async (
  payload: RemoveTalentComment,
  { requestId }: Options,
): Promise<EventPayload<{}>> => {
  const params = {
    requestId,
    auth: true,
    body: payload,
  };

  return await api
    .post<EventPayload<{}>[]>("/matching/cmd/removeTalentComment", params)
    .then((res) => res.data[0]);
};

export const postUpdateTalentAccount = async (
  payload: UpdateTalentAccount,
  { requestId }: Options,
): Promise<any> => {
  const params = {
    requestId,
    auth: true,
    body: {
      ...payload,
    },
  };

  return await api
    .post<
      EventPayload<Partial<TalentAccount>>[]
    >("/identity/cmd/updateTalentAccount", params)
    .then((res) => res.data);
};

export const searchCertificates = (filters: CertificateFilters) => {
  return api.get<Certificate[]>("/projections/qry/certificates", {
    requestId: nanoid(),
    queryParams: {
      ...filters,
    },
    auth: true,
  });
};

export const postAddTalentCertificate = async (
  body: AddTalentCertificate,
  { requestId }: Options,
): Promise<EventPayload<TalentCertificateAdded>[]> => {
  const params = {
    requestId,
    auth: true,
    body,
  };

  return await api
    .post<
      EventPayload<TalentCertificateAdded>[]
    >("/matching/cmd/addTalentCertificate", params)
    .then((res) => res.data);
};

export const certificateDocumentUploadUrl = (
  queryParams: CertificateDocumentUploadUrlQuery,
  { requestId }: Options,
): Promise<DocumentUploadUrl> => {
  return api
    .get<DocumentUploadUrl>("/matching/qry/certificateDocumentUploadUrl", {
      auth: true,
      requestId,
      queryParams: {
        ...queryParams,
      },
    })
    .then((res) => res.data);
};

export const certificateDocumentDownloadUrl = (
  queryParams: CertificateDocumentDownloadUrlQuery,
  { requestId }: Options,
): Promise<DocumentDownloadUrl> => {
  return api
    .get<DocumentDownloadUrl>("/matching/qry/documentDownloadUrl", {
      auth: true,
      requestId,
      queryParams: {
        ...queryParams,
      },
    })
    .then((res) => res.data);
};

export const talentProfilePictureDownloadUrl = (
  queryParams: TalentProfilePictureDownloadUrlQuery,
  { requestId }: Options,
): Promise<TalentProfilePictureDownloadUrl> => {
  return api
    .get<TalentProfilePictureDownloadUrl>(
      "/identity/qry/talentProfilePictureDownloadUrl",
      {
        auth: true,
        requestId,
        queryParams: {
          ...queryParams,
        },
      },
    )
    .then((res) => res.data);
};

export const certificateUpload = (url: string, content: Blob) =>
  api
    .upload(url, {
      headers: {},
      body: content,
    })
    .then((res) => res.data);

export const postUpdateTalentCertificate = async (
  body: UpdateTalentCertificate,
  { requestId }: Options,
): Promise<EventPayload<TalentCertificateUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body,
  };
  return await api
    .post<
      EventPayload<TalentCertificateUpdated>[]
    >("/matching/cmd/updateTalentCertificate", params)
    .then((res) => res.data[0]);
};

export const postRemoveTalentCertificate = async (
  body: RemoveTalentCertificate,
  { requestId }: Options,
): Promise<EventPayload<TalentCertificateRemoved>> => {
  const params = {
    requestId,
    auth: true,
    body,
  };
  return await api
    .post<
      EventPayload<TalentCertificateRemoved>[]
    >("/matching/cmd/removeTalentCertificate", params)
    .then((res) => res.data[0]);
};

export const postRemoveTalentCertificateDocument = async (
  body: RemoveTalentCertificateDocument,
  { requestId }: Options,
): Promise<EventPayload<TalentCertificateDocumentRemoved>[]> => {
  const params = {
    requestId,
    auth: true,
    body,
  };
  return await api
    .post<
      EventPayload<TalentCertificateDocumentRemoved>[]
    >("/matching/cmd/removeTalentCertificateDocument", params)
    .then((res) => res.data);
};

export const postApproveTalentCertificate = async (
  body: ApproveTalentCertificate,
  { requestId }: Options,
): Promise<EventPayload<TalentCertificateApproved>> => {
  const params = {
    requestId,
    auth: true,
    body,
  };
  return await api
    .post<
      EventPayload<TalentCertificateDocumentRemoved>[]
    >("/matching/cmd/approveTalentCertificate", params)
    .then((res) => res.data[0]);
};

export const postRejectTalentCertificate = async (
  body: RejectTalentCertificate,
  { requestId }: Options,
): Promise<EventPayload<TalentCertificateRejected>> => {
  const params = {
    requestId,
    auth: true,
    body,
  };
  return await api
    .post<
      EventPayload<TalentCertificateRejected>[]
    >("/matching/cmd/rejectTalentCertificate", params)
    .then((res) => res.data[0]);
};

export const postAddTalentTraining = async (
  body: AddTalentTraining,
  { requestId }: Options,
): Promise<EventPayload<TalentTrainingAdded>> => {
  const params = {
    requestId,
    auth: true,
    body,
  };

  return await api
    .post<
      EventPayload<TalentTrainingAdded>[]
    >("/matching/cmd/addTalentTraining", params)
    .then((res) => res.data[0]);
};

export const postUpdateTalentTraining = async (
  body: UpdateTalentTraining,
  { requestId }: Options,
): Promise<EventPayload<TalentTrainingUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body,
  };

  return await api
    .post<
      EventPayload<TalentTrainingUpdated>[]
    >("/matching/cmd/updateTalentTraining", params)
    .then((res) => res.data[0]);
};

export const postRemoveTalentSsn = (
  body: RemoveTalentPaymentInformation,
  options: Options,
) =>
  command<RemoveTalentPaymentInformation, Record<string, never>>(
    "/identity/cmd/removeTalentSsn",
    body,
    options,
  );

export const postRemoveTalentBankInfo = (
  body: RemoveTalentPaymentInformation,
  options: Options,
) =>
  command<RemoveTalentPaymentInformation, Record<string, never>>(
    "/identity/cmd/removeTalentBankInfo",
    body,
    options,
  );

export const postUpdateTalentSalaryInformation = async (
  body: UpdateTalentSalaryInformation,
  { requestId }: Options,
): Promise<EventPayload<TalentSalaryInformationUpdated>[]> => {
  const params = {
    requestId,
    auth: true,
    body,
  };

  return await api
    .post<
      EventPayload<TalentSalaryInformationUpdated>[]
    >("/identity/cmd/updateTalentSalaryInformation", params)
    .then((res) => res.data);
};

export const postUpdateTalentTaxInformation = async (
  body: UpdateTalentTaxInformation,
  { requestId }: Options,
): Promise<EventPayload<TalentTaxInformationUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body,
  };

  return await api
    .post<
      EventPayload<TalentTaxInformationUpdated>[]
    >("/identity/cmd/updateTalentTaxInformation", params)
    .then((res) => res.data[0]);
};

export const postUpdateTalentTaxDeductionCard = async (
  body: UpdateTalentTaxDeductionCard,
  { requestId }: Options,
): Promise<EventPayload<TalentTaxDeductionCardUpdated>> => {
  const params = {
    requestId,
    auth: true,
    body,
  };

  return await api
    .post<
      EventPayload<TalentTaxDeductionCardUpdated>[]
    >("/identity/cmd/updateTalentTaxDeductionCard", params)
    .then((res) => res.data[0]);
};

export const postRemoveTalentTraining = async (
  body: RemoveTalentTraining,
  { requestId }: Options,
): Promise<EventPayload<TalentTrainingRemoved>> => {
  const params = {
    requestId,
    auth: true,
    body,
  };

  return await api
    .post<
      EventPayload<TalentTrainingRemoved>[]
    >("/matching/cmd/removeTalentTraining", params)
    .then((res) => res.data[0]);
};

export const talentHrRepresentativesAutocomplete = (
  filters: TalentHrRepresentativesAutocompleteFilters,
  { requestId }: Options,
) =>
  api
    .get<TalentHrRepresentativeAutocomplete[]>(
      "/projections/qry/hrRepresentativesAutocomplete",
      {
        requestId,
        auth: true,
        queryParams: {
          search: filters.search,
          enabled:
            filters.enabled != null ? filters.enabled.toString() : "true",
        },
      },
    )
    .then((res) => res.data);

export const updateTalentHrRepresentative = (
  body: UpdateTalentHrRepresentative,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<TalentHrRepresentativeUpdated>[]>(
      "/matching/cmd/updateTalentHrRepresentative",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data[0]);

export const updateTalentEezyReview = (
  body: UpdateTalentEezyReview,
  { requestId }: Options,
) =>
  api
    .post<EventPayload<TalentEezyReviewUpdated>[]>(
      "/matching/cmd/updateTalentEezyReview",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data[0]);

export const getManualSalariesByEmployment = (employmentId: string) => {
  return api
    .get<ManualSalariesResponse>(
      "/projections/qry/manualSalariesByEmployment",
      {
        requestId: nanoid(),
        queryParams: {
          employmentId,
        },
        auth: true,
      },
    )
    .then((res) => res.data);
};

export const getEmploymentDetailsForManualSalary = (employmentId: string) => {
  return api
    .get<EmploymentDetailsForManualSalaryResponse>(
      "/projections/qry/empDetailsForManSalary2",
      {
        requestId: nanoid(),
        queryParams: {
          employmentId,
        },
        auth: true,
      },
    )
    .then((res) => res.data);
};

export const postAddManualSalary = async (
  manualSalaryData: CreateManualSalary,
  { requestId }: Options,
): Promise<EventPayload<ManualSalaryAdded>> => {
  return await api
    .post<EventPayload<ManualSalaryAdded>[]>("/working/cmd/crtManSalary", {
      requestId,
      auth: true,
      body: {
        ...manualSalaryData,
      },
    })
    .then((res) => {
      return res.data[0];
    });
};

export const postEditManualSalary = async (
  manualSalaryData: EditManualSalary,
  { requestId }: Options,
): Promise<EventPayload<ManualSalaryEdited>> => {
  return await api
    .post<EventPayload<ManualSalaryEdited>[]>("/working/cmd/editManSalary", {
      requestId,
      auth: true,
      body: {
        ...manualSalaryData,
      },
    })
    .then((res) => {
      return res.data[0];
    });
};

export const postDeleteManualSalary = async (
  manualSalaryData: DeleteManualSalary,
  { requestId }: Options,
): Promise<EventPayload<{}>> => {
  return await api
    .post<EventPayload<{}>[]>("/working/cmd/delManSalary", {
      requestId,
      auth: true,
      body: {
        ...manualSalaryData,
      },
    })
    .then((res) => {
      return res.data[0];
    });
};

export const getEezyCompanyAutocomplete = (
  eezyCompanyFilters: EezyCompanyAutocompleteFilters,
  { requestId }: Options = {},
) => {
  return api
    .get<EezyCompany[]>("/projections/qry/eezyCompanyAutocomplete", {
      requestId,
      auth: true,
      queryParams: {
        search: eezyCompanyFilters.name,
      },
    })
    .then((res) => {
      return res.data;
    });
};

export const runTradeUnionsAutocompleteQuery = (
  tradeUnionsFilters: TradeUnionsAutocompleteFilters,
  { requestId }: Options,
) => {
  return api
    .get<any>("/projections/qry/tradeUnionsAutocomplete", {
      requestId,
      auth: true,
      queryParams: {
        search: tradeUnionsFilters.name,
      },
    })
    .then((res) => {
      return res.data;
    });
};

export const chgAssignmentOrdAttr = async (
  body: ChangeAssignmentOrderAttributes,
  { requestId }: Options,
): Promise<EventPayload<AssignmentOrderAttributeEvents>[]> =>
  await api
    .post<EventPayload<AssignmentOrderAttributeEvents>[]>(
      "/working/cmd/chgAssignmentOrdAttr",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const chgAssignmentCostCenters = async (
  body: ChangeAssignmentCostCenters,
  { requestId }: Options,
): Promise<EventPayload<AssignmentCostCentersChanged>> =>
  await api
    .post<EventPayload<AssignmentCostCentersChanged>[]>(
      "/working/cmd/chgAssignmentCostCenters",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data[0]);

export const chgAssignmentSalaryOverride = async (
  body: ChangeAssignmentSalaryOverride,
  { requestId }: Options,
): Promise<EventPayload<AssignmentSalaryOverrideChanged>> =>
  await api
    .post<EventPayload<AssignmentSalaryOverrideChanged>[]>(
      "/working/cmd/chgAssignmentSalaryOverride",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data[0]);

export const chgAssignmentInvPrice = async (
  body: ChangeAssignmentInvPrice,
  { requestId }: Options,
): Promise<EventPayload<AssignmentInvPriceChanged>> =>
  await api
    .post<EventPayload<AssignmentInvPriceChanged>[]>(
      "/working/cmd/chgAssignmentInvPrice",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data[0]);

export const endAssignment = async (
  body: SetAssignmentEnd,
  { requestId }: Options,
): Promise<EventPayload<AssignmentRangeUpdated>> =>
  await api
    .post<EventPayload<AssignmentRangeUpdated>[]>(
      "/working/cmd/endAssignment",
      {
        requestId,
        auth: true,
        body,
      },
    )
    // TODO is this a proper way to handle the response?
    .then((res) => res.data[0]);

export const updateAssignmentRange = async (
  body: UpdateAssignmentRange,
  { requestId }: Options,
): Promise<EventPayload<AssignmentRangeUpdated>> =>
  await api
    .post<EventPayload<AssignmentRangeUpdated>[]>(
      "/working/cmd/updateAssignmentRange",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data[0]);

export const updateContinuousEmploymentRange = async (
  body: UpdateContinuousEmploymentRange,
  { requestId }: Options,
): Promise<EventPayload<unknown>[]> =>
  await api
    .post<EventPayload<unknown>[]>(
      "/working/cmd/updateContinuousEmploymentRange",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const updateTalentVehicleAvailable = async (
  body: UpdateTalentVehicleAvailable,
  { requestId }: Options,
): Promise<EventPayload<TalentVehicleAvailableUpdated>[]> =>
  await api
    .post<EventPayload<TalentVehicleAvailableUpdated>[]>(
      "/matching/cmd/updateTalentVehicleAvailable",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const assignmentsByTalentId = (
  filters: AssignmentsByTalentIdFilters,
  { requestId }: Options = {},
) => {
  return api
    .get<AssignmentById[]>("/projections/qry/assignmentsByTalentId", {
      requestId,
      auth: true,
      queryParams: {
        ...filters,
      },
    })
    .then((res) => res.data);
};

export const overlappableAssignmentsByTalentId = (
  filters: OverlappableAssignmentsByTalentIdFilters,
  { requestId }: Options = {},
) =>
  api
    .get<OverlappableContEmp[]>(
      "/projections/qry/overlappableAssignmentsByTalentId",
      {
        requestId,
        auth: true,
        queryParams: {
          bundleId: filters.bundleId,
          talentIds: filters.talentIds.join(","),
        },
      },
    )
    .then((response) => response.data);

export const blacklistsByTalentId = async (talentId: string) =>
  api
    .get<TalentBlacklist[]>("/projections/qry/blacklistsByTalentId", {
      requestId: nanoid(),
      auth: true,
      queryParams: {
        talentId,
      },
    })
    .then((res) => res.data);

export const postAddBlacklistEntry = async (
  body: AddBlacklistEntry,
): Promise<EventPayload<BlacklistEntryAdded>> => {
  return await api
    .post<EventPayload<BlacklistEntryAdded>[]>(
      "/order/cmd/createBlacklistEntry",
      {
        requestId: nanoid(),
        auth: true,
        body,
      },
    )
    .then((res) => res.data[0]);
};

export const postRemoveBlacklistEntry = async (
  body: RemoveBlacklistEntry,
): Promise<EventPayload<BlacklistEntryRemoved>> => {
  return await api
    .post<EventPayload<BlacklistEntryRemoved>[]>(
      "/order/cmd/removeBlacklistEntry",
      {
        requestId: nanoid(),
        auth: true,
        body,
      },
    )
    .then((res) => res.data[0]);
};

export const updateBlacklistEntryCmd = (
  body: UpdateBlacklistEntry,
  options: Options = {},
): Promise<EventPayload<BlacklistEntryUpdated>[]> =>
  command<UpdateBlacklistEntry, BlacklistEntryUpdated>(
    "/order/cmd/updateBlacklistEntry",
    body,
    options,
  );

export const exclusivitiesByTalentId = (
  params: ExclusivitiesByTalentIdFilters,
  options: Options = {},
): Promise<Paginated<TalentExclusivity[]>> =>
  query<ExclusivitiesByTalentIdFilters, Paginated<TalentExclusivity[]>>(
    "/projections/qry/exclusivitiesByTalentId",
    params,
    options,
  );

export const createExclusivityEntryCmd = (
  body: CreateExclusivityEntry,
  options: Options = {},
): Promise<EventPayload<ExclusivityEntryAdded>[]> =>
  command<CreateExclusivityEntry, ExclusivityEntryAdded>(
    "/order/cmd/createExclusivityEntry",
    body,
    options,
  );

export const updateExclusivityEntryCmd = (
  body: UpdateExclusivityEntry,
  options: Options = {},
): Promise<EventPayload<ExclusivityEntryUpdated>[]> =>
  command<UpdateExclusivityEntry, ExclusivityEntryUpdated>(
    "/order/cmd/updateExclusivityEntry",
    body,
    options,
  );

export const removeExclusivityEntryCmd = (
  body: RemoveExclusivityEntry,
  options: Options = {},
): Promise<EventPayload<ExclusivityEntryRemoved>[]> =>
  command<RemoveExclusivityEntry, ExclusivityEntryRemoved>(
    "/order/cmd/removeExclusivityEntry",
    body,
    options,
  );

export const setTalentRecruiter = async (
  body: SetTalentRecruiter,
  { requestId }: Options,
): Promise<EventPayload<TalentRecruiterUpdated>[]> =>
  await api
    .post<EventPayload<TalentRecruiterUpdated>[]>(
      "/order/cmd/setTalentRecruiter",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const postUpdateTalentStatus = async (
  body: UpdateTalentStatus,
  options: Options = {},
): Promise<EventPayload<TalentStatusUpdated>[]> =>
  command<UpdateTalentStatus, TalentStatusUpdated>(
    "/identity/cmd/updateTalentStatus",
    body,
    options,
  );

export const getTalentJobApplications = (
  params: TalentJobApplicationsParams,
  options: Options = {},
): Promise<TalentJobApplicationsResponse> =>
  query<TalentJobApplicationsParams, TalentJobApplicationsResponse>(
    "/projections/qry/talentJobApplications",
    params,
    options,
  );

export const candidateIntroductions = (
  params: CandidateIntroductionsByTalentIdFilters,
  options: Options = {},
): Promise<Paginated<TalentIntroduction[]>> =>
  query<
    CandidateIntroductionsByTalentIdFilters,
    Paginated<TalentIntroduction[]>
  >("/projections/qry/candidateIntroductionsByTalentId", params, options);

export const introduceTalentsToCustomerWithoutOrder = async (
  body: IntroduceTalentsToCustomerWithoutOrder,
  options: Options = {},
): Promise<EventPayload<CandidateWithoutOrderIntroducedToCustomer>[]> =>
  command<
    IntroduceTalentsToCustomerWithoutOrder,
    CandidateWithoutOrderIntroducedToCustomer
  >("/order/cmd/introduceTalentsToCustomerWithoutOrder", body, options);

export const cancelTalentIntroductionWithoutOrder = async (
  body: CancelTalentIntroductionWithoutOrder,
  options: Options = {},
): Promise<EventPayload<CandidateWithoutOrderIntroductionCancelled>[]> =>
  command<
    CancelTalentIntroductionWithoutOrder,
    CandidateWithoutOrderIntroductionCancelled
  >("/order/cmd/cancelTalentIntroductionWithoutOrder", body, options);

export const approveIntroducedTalentWithoutOrder = async (
  body: ApproveIntroducedTalentWithoutOrder,
  options: Options = {},
): Promise<EventPayload<CandidateWithoutOrderApprovedByCustomer>[]> =>
  command<
    ApproveIntroducedTalentWithoutOrder,
    CandidateWithoutOrderApprovedByCustomer
  >("/order/cmd/approveIntroducedTalentWithoutOrder", body, options);

export const setTalentWorkingAreaFromHome = async (
  body: SetTalentWorkingAreaFromHome,
  options: Options = {},
): Promise<EventPayload<TalentWorkingAreaEvents>[]> =>
  command<SetTalentWorkingAreaFromHome, TalentWorkingAreaEvents>(
    "/matching/cmd/setTalentWorkingAreaFromHome",
    body,
    options,
  );

export const getTalentSelfServiceOrderPermissions = (
  params: TalentIdFilter,
  options: Options = {},
): Promise<TalentSelfServiceOrderPermissionsResponse> =>
  query<TalentIdFilter, TalentSelfServiceOrderPermissionsResponse>(
    "/projections/qry/talentSelfServiceOrderPermissions",
    params,
    options,
  );

export const addSelfServiceOrderPermission = (
  body: AddSelfServiceOrderPermission,
  options: Options = {},
) =>
  command<AddSelfServiceOrderPermission, SelfServiceOrderPermissionAdded>(
    "/order/cmd/addSelfServiceOrderPermission",
    body,
    options,
  );

export const updateSelfServiceOrderPermission = (
  body: UpdateSelfServiceOrderPermission,
  options: Options = {},
) =>
  command<UpdateSelfServiceOrderPermission, SelfServiceOrderPermissionUpdated>(
    "/order/cmd/updateSelfServiceOrderPermission",
    body,
    options,
  );

export const removeSelfServiceOrderPermission = (
  body: RemoveSelfServiceOrderPermission,
  options: Options = {},
) =>
  command<
    RemoveSelfServiceOrderPermission,
    TalentSelfServiceOrderPermissionDeleted
  >("/order/cmd/removeSelfServiceOrderPermission", body, options);

export const getWorkAgreementTemplateData = async (
  params: WorkAgreementTemplateDataParams,
  options: Options = {},
): Promise<WorkAgreementTemplateData> =>
  query<WorkAgreementTemplateDataParams, WorkAgreementTemplateData>(
    "/projections/qry/expertWorkAgreementTemplateData",
    params,
    options,
  );

export const certificateDefinitionsAutoCompleteQuery = (
  filters: CertificateDefinitionsFilters,
  options: Options = {},
): Promise<CertificateDefinition[]> =>
  query<CertificateDefinitionsFilters, CertificateDefinition[]>(
    "/projections/qry/certificates",
    filters,
    options,
  );

export const trainingDefinitionsQuery = (
  filters: TrainingDefinitionsFilters,
  options: Options = {},
): Promise<TrainingDefinition[]> =>
  query<TrainingDefinitionsFilters, TrainingDefinition[]>(
    "/projections/qry/trainingDefinitions",
    filters,
    options,
  );

export const getTalentCVDownloadUrl = (
  params: TalentCvParams,
  options: Options = {},
): Promise<DocumentDownloadUrl> =>
  query<TalentCvParams, DocumentDownloadUrl>(
    "/identity/qry/talentCvDownloadUrl",
    params,
    options,
  );

export const getTalentCVUploadUrl = (
  params: CvDocumentUploadUrlQuery,
  options: Options = {},
): Promise<DocumentUploadUrl> =>
  query<CvDocumentUploadUrlQuery, DocumentUploadUrl>(
    "/identity/qry/talentCvUploadUrl",
    params,
    options,
  );

export const uploadTalentCV = (url: string, content: Blob) => {
  return api.upload(url, {
    headers: {},
    body: content,
  });
};

export const removeTalentCVDocument = (
  body: TalentCvParams,
  options: Options = {},
): Promise<any> => {
  const { talentId, pathspec } = body;
  const { requestId } = options;
  return api.post<any>("/identity/cmd/removeTalentCv", {
    requestId,
    auth: true,
    body: {
      id: talentId,
      pathspec,
    },
  });
};

export const getTalentLogs = (
  params: TalentIdQueryParameter,
  options: Options = {},
): Promise<any> =>
  query<TalentIdQueryParameter, TalentLogEvent[]>(
    "/identity/qry/talentLog",
    params,
    options,
  );
